import { useMemo } from 'react'
import { sample, upperFirst } from 'lodash'

import { selectIsCurrentUserScorp } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { Link, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'

const deductions = {
  standard: [
    'your Heard subscription fees',
    'payment processor fees (e.g. Square, Stripe)',
    'booking and billing software',
    'tax preparation fees',
    'professional liability insurance',
    'general liability insurance',
    'bank fees (e.g. overdraft and maintenance)',
    'business registration and license fees',
    'depreciation of office furniture (exceeding $2,500)',
    'legal fees',
  ],
  marketing: [
    'Mail and print ads',
    'Online advertising',
    'Website design',
    'Website hosting fees',
    'Professional headshots',
    'Logo design',
    'Search engine optimization tools',
    'Business cards',
    'Marketing brochures',
    'Paid sponsorships',
    'Promotional items (e.g. pens or notepads)',
    'Monthly fees for directory listings (e.g. Psychology Today)',
    'Postage and freight',
  ],
  travel: [
    'baggage fees',
    'rental car costs',
    'rideshare and taxi services',
    'laundry and dry cleaning',
    '50% of meals eaten',
    'wifi access',
    'equipment rental for presentations',
    'lodging',
    'personal vehicle use and maintenance',
  ],
  meals: [
    'coffee or meals with business partners or co-workers',
    'meals for employees and contractors',
    'lunch meetings with potential clients',
    'networking event meals',
  ],
  education: [
    'professional conferences',
    'clinical trainings',
    'seminars and workshops',
  ],
  educationSupplies: [
    'tuition, books, and supplies',
    'online courses',
    'tutors, typists, or researchers',
  ],
  supervision: [
    'training or lessons to maintain your professional certification',
    'upgrading your level of certification within your field',
  ],
  housing: [
    'rent or leases for your office',
    'utilities (heat, water, electricity, internet, phone) for your office',
    'office moving expenses',
    'mortgage payments and utilities for a home office',
  ],
  officeSupplies: [
    'stationery',
    'computer equipment',
    'printers and photocopiers',
    'phones',
    'printer or photocopier ink or toner',
    'printer paper',
    'small furniture items',
    'artwork',
    'storage bins',
  ],
  aids: [
    'toys and games',
    'art therapy supplies and equipment',
    'pressure vests and weighted blankets',
    'prompt cards',
    'instruments for music therapy',
    'workbooks and books',
    'CDs and DVDs',
  ],
}

export const TaxDeductionTipCard = () => {
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, false)

  // Create tips with dynamic therapist text
  const deductionTips = useMemo(
    () => [
      {
        tip: (deduction: string) => (
          <>
            You can write off <b>{deduction}</b> as a deductible expense.
          </>
        ),
        deductions: deductions.standard,
        articleLink:
          'https://www.joinheard.com/articles/the-complete-list-of-tax-deductions-for-therapists',
      },
      {
        tip: (deduction: string) => (
          <>
            <b>{upperFirst(deduction)}</b> can be deducted for business travel
            that&apos;s directly related to your practice.
          </>
        ),
        deductions: deductions.travel,
        hideForScorp: true,
        articleLink:
          'https://www.joinheard.com/articles/what-therapists-need-to-know-about-deducting-business-travel',
      },
      {
        tip: (deduction: string) => (
          <>
            <b>{upperFirst(deduction)}</b> with a prospective or existing client
            can be deducted at 50%.
          </>
        ),
        deductions: deductions.meals,
        articleLink:
          'https://www.joinheard.com/articles/what-therapists-need-to-know-about-deducting-business-meals',
      },
      {
        tip: (deduction: string) => (
          <>
            As a certified {replacementText}, you can write off{' '}
            <b>{deduction}</b> as a continuing education deductible expense, as
            well as <b>travel to and from these events.</b>
          </>
        ),
        deductions: deductions.education,
        articleLink:
          'https://www.joinheard.com/articles/what-therapists-need-to-know-about-deducting-education-expenses',
      },
      {
        tip: (deduction: string) => (
          <>
            As a licensed {replacementText}, you can write off{' '}
            <b>{deduction}</b> as a continuing education deductible expense.
          </>
        ),
        deductions: deductions.educationSupplies,
        articleLink:
          'https://www.joinheard.com/articles/what-therapists-need-to-know-about-deducting-education-expenses',
      },
      {
        tip: () => (
          <>
            <b>Personal therapy</b> for yourself counts as continuing education,
            and can be claimed as a tax deduction, including{' '}
            <b>travel to your sessions</b>.
          </>
        ),
        deductions: [],
        articleLink:
          'https://www.joinheard.com/articles/is-going-to-therapy-tax-deductible',
      },
      {
        tip: (deduction: string) => (
          <>
            As a certified {replacementText}, clinical supervision can be
            written off as a<b> {deduction}</b>.
          </>
        ),
        deductions: deductions.supervision,
        articleLink:
          'https://www.joinheard.com/articles/is-clinical-supervision-tax-deductible-for-therapists',
      },
      {
        tip: () => (
          <>
            If you&apos;re working from home, your <b>home office</b> likely
            qualifies as a tax deduction.
          </>
        ),
        deductions: [],
        articleLink:
          'https://www.joinheard.com/articles/what-therapists-need-to-know-about-the-home-office-deduction',
      },
    ],
    [replacementText]
  )

  const randomTip = useMemo(
    () =>
      sample(
        deductionTips.filter(({ hideForScorp }) => !hideForScorp || !isScorp)
      ),
    [isScorp, deductionTips]
  )

  const tipText = useMemo(() => {
    if (!randomTip) {
      return ''
    }

    const tip = randomTip.tip

    const randomDeduction =
      randomTip.deductions.length > 0
        ? randomTip.deductions[
            Math.floor(Math.random() * randomTip.deductions.length)
          ]
        : ''

    return tip(randomDeduction)
  }, [randomTip])

  return (
    <div className="p-6 space-y-2">
      <Text variant={TypographyStyles.H3}>Tax Deduction Tip</Text>

      <div>
        <Text className="inline">{tipText}</Text>{' '}
        <Link
          href={randomTip?.articleLink ?? ''}
          newPage
          className="inline"
          trackMessage="Accessed Tax Deduction Tip article"
        >
          View article →
        </Link>
      </div>
    </div>
  )
}
